import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SnackbarService } from '@bcl/design-system';
import { Monitor, MonitorWithOptions } from 'src/app/pages/area/area.model';
import { AreaApiService } from 'src/app/pages/area/area.service';
import { BaseAreaMonitorAccordionComponent } from '../base-accordion.component';
import { AccordionService } from 'src/app/services/accordion.service';
import { MatDialog } from '@angular/material/dialog';
import { ReplaceMonitorDialogComponent } from '../replace-monitor-dialog/replace-monitor-dialog.component';
import {
  ReplaceMonitorDialogData,
  ReplaceMonitorDialogResponse,
} from '../replace-monitor-dialog/replace-monitor-dialog.model';
import { AreaMapApiService } from 'src/app/pages/area-map/area-map-api.service';
import { DictionaryService } from 'src/app/services/dictionary.service';
import { combineLatest, of, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-area-monitor-actions',
  templateUrl: './area-monitor-actions.component.html',
  styleUrls: ['./area-monitor-actions.component.scss'],
})
export class AreaMonitorActionsComponent extends BaseAreaMonitorAccordionComponent {
  @Input() monitor: MonitorWithOptions;
  @Input() visitId: number;
  @Input() date: string;
  @Input() dayNumber: string;
  @Input() areaId: number;
  @Input() visit?: any;
  @Input() defaultCar?: number | null;
  @Input() isEditingDisabled = false;
  @Input() isProcessing = false;
  @Input() options: any;
  @Output() monitorStatusChange = new EventEmitter<boolean>();
  @Output() editMonitor = new EventEmitter<void>();
  @Output() monitorReplaced = new EventEmitter<number | undefined>();

  constructor(
    private areaApiService: AreaApiService,
    private snackbarService: SnackbarService,
    accordionService: AccordionService,
    private dialog: MatDialog,
    private areaMapService: AreaMapApiService,
    private dictionaryService: DictionaryService
  ) {
    super(accordionService);
  }

  get isFormDisabled(): boolean {
    return this.isEditingDisabled || this.isProcessing;
  }
  public monitorToggleStatus(status: boolean): void {
    this.monitorStatusChange.emit(status);
  }
  public edit() {
    this.editMonitor.emit();
  }

  public replaceTrap(): void {
    if (!this.options || !this.options.monitor) {
      this.snackbarService.open(
        'red',
        'MONITOR.DETAILS.MESSAGES.OPTIONS_NOT_LOADED'
      );
      return;
    }

    const sources = {
      instruments: this.dictionaryService
        .getDictionaryItemsByKeyV2('TABLE.PC.MONITOR.INSTRUMENTS')
        .pipe(catchError(() => of(null))),
      parts: this.areaMapService
        .getPartsForArea(this.areaId, this.visitId)
        .pipe(
          catchError((error) => {
            console.error('Error loading contract parts:', error);
            return of([]);
          })
        ),
    };

    combineLatest(sources)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ instruments, parts }) => {
        let instrumentOptions: any[] = [];
        let partOptions: any[] = [];

        if (instruments) {
          instrumentOptions = instruments.Data.map((x) => ({
            label: this.options.monitor.types.find(
              (t) => t.value === x.instrumentId.toString()
            )?.label,
            instrumentId: x.instrumentId,
            moduleId: x.moduleId,
            value: x.value,
            token: this.options.monitor.types.find(
              (t) => t.value === x.instrumentId.toString()
            )?.token,
          }));
        }

        if (parts && parts.length > 0) {
          partOptions = parts;
        }

        const matchingContractPart = partOptions.find(
          (part) => Number(part.moduleId) === Number(this.monitor.moduleId)
        );
        const selectedContractPartId =
          matchingContractPart?.contractPartId || 0;

        const dialogData: ReplaceMonitorDialogData = {
          areaId: this.areaId,
          oldMonitorId: this.monitor.id,
          instrumentOptions,
          partOptions,
          statusOptions: this.options.monitor.statuses.filter((status) =>
            ['1', '2'].includes(status.value)
          ),
          warehouses: this.options.monitor.product.warehouses,
          products: this.options.monitor.product.products,
          units: this.options.monitor.product.units,
          defaultCar: this.defaultCar,
          selectedContractPartId,
          moduleId: this.monitor.moduleId,
        };

        const dialogRef = this.dialog.open(ReplaceMonitorDialogComponent, {
          data: dialogData,
        });

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.destroyed$))
          .subscribe((result: ReplaceMonitorDialogResponse) => {
            if (result) {
              result.startDate = new Date()
                .toLocaleString('sv')
                .replace(' ', 'T');
              this.replaceMonitor(result);
            }
          });
      });
  }

  private replaceMonitor(monitor: ReplaceMonitorDialogResponse): void {
    this.areaMapService
      .replaceMonitor(monitor)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (response) => {
          this.snackbarService.open(
            'green',
            'MONITOR.DETAILS.MESSAGES.MONITOR_REPLACED_SUCCESSFULLY'
          );

          const newMonitorId = response?.id;

          if (newMonitorId) {
            this.monitorReplaced.emit(newMonitorId);
          } else {
            this.monitorReplaced.emit();
          }
        },
        () => {
          this.snackbarService.open(
            'red',
            'MONITOR.DETAILS.MESSAGES.MONITOR_REPLACE_ERROR'
          );
        }
      );
  }
}
